/**
 * Company: SPHMedia
 * Description: [Reaction toolbar hook]
 */

import type { HandleReactionClick } from "@typings/Reactions";
import { useCallback, useEffect, useState } from "react";

type UseReactionToolbarData = {
  isReactionExpanded: boolean;
  toggleExpand: () => void;
  handleReactionSelect: HandleReactionClick;
};

type UseReactionToolbarProps = {
  articleId: string;
  handleReactionClick: HandleReactionClick;
  isExpanded?: boolean;
  onExpand?: (articleId: string | null) => void;
};

/**
 * Custom React Hook: useReactionToolbar
 * Manages reaction toolbar expansion state and interaction handling
 * @param props - Configuration object with articleId, handlers, and expansion state
 * @returns Object containing expansion state, toggle handler, and reaction selector
 */
export function useReactionToolbar({
  articleId,
  handleReactionClick,
  isExpanded = false,
  onExpand,
}: UseReactionToolbarProps): UseReactionToolbarData {
  const [isReactionExpanded, setIsReactionExpanded] = useState(isExpanded);

  const toggleExpand = useCallback(() => {
    setIsReactionExpanded((prev) => {
      const newState = !prev;
      onExpand?.(newState ? articleId : null);
      return newState;
    });
  }, [articleId, onExpand]);

  const handleReactionSelect = useCallback(
    async (reactionId: string, variant?: "top" | "bottom") => {
      await handleReactionClick(reactionId, variant);
      // Collapse after selecting
      setIsReactionExpanded(false);
      onExpand?.(null);
    },
    [handleReactionClick, onExpand],
  );

  // Sync local state with parent prop
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsReactionExpanded(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    const handleOutsideClick = (event: Event) => {
      const el = event.target as HTMLElement | null;
      if (
        !el ||
        (!el.classList.contains("interaction-toolbar") &&
          !el.closest(".interaction-toolbar"))
      ) {
        setIsReactionExpanded(false);
        onExpand?.(null);
      }
    };

    const options: AddEventListenerOptions = { passive: true };
    globalThis.addEventListener("mouseup", handleOutsideClick, options);
    globalThis.addEventListener("touchend", handleOutsideClick, options);

    return () => {
      globalThis.removeEventListener("mouseup", handleOutsideClick, options);
      globalThis.removeEventListener("touchend", handleOutsideClick, options);
    };
  }, [onExpand]);

  return {
    isReactionExpanded,
    toggleExpand,
    handleReactionSelect,
  };
}
